import { fetchOpenAIText } from "../utils/openaiClient";
import { getAudioFileFromUrl, generateTranscript, enhanceTranscript } from "../utils/transcriptService";

const isYouTubeLink = (url) => /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/.test(url);

export function useWarmUpDiscussionGeneratorLogic({
  warmUpDiscussionInput, setWarmUpDiscussionInput, warmUpDiscussionLevel,
  warmUpDiscussionCount, setWarmUpDiscussionCount,
  setWarmUpDiscussionLoading, generatedWarmUpDiscussion, setGeneratedWarmUpDiscussion,
  currentTab, onInsertToBoard, activeEditor,
  setShowWarmUpDiscussionGenerator, setSelectedTemplate,
}) {
  const handleWarmUpDiscussionGenerate = async () => {
    if (!warmUpDiscussionInput.trim()) return;

    setWarmUpDiscussionLoading(true);
    try {
      // If input is a YouTube link or media URL, try to get transcript
      let sourceText = warmUpDiscussionInput;
      const isMedia = /^(https?:\/\/).+\.(mp3|wav|m4a|mp4|mov|webm)(\?.*)?$/i.test(warmUpDiscussionInput);
      if (isYouTubeLink(warmUpDiscussionInput)) {
        // Minimal approach: include URL and ask model to infer topic (no direct fetch of captions here)
        sourceText = `Video URL: ${warmUpDiscussionInput}`;
      } else if (isMedia) {
        try {
          const file = await getAudioFileFromUrl(warmUpDiscussionInput);
          const raw = await generateTranscript(file);
          const improved = await enhanceTranscript(raw);
          sourceText = improved.slice(0, 12000); // keep prompt size sane
        } catch (e) {
          sourceText = `Media URL: ${warmUpDiscussionInput}`;
        }
      }

      const prompt = `Create warm-up discussion questions for the following topic, text, or video. Generate exactly ${warmUpDiscussionCount} engaging questions that will help students think about the topic and prepare for further discussion. The level should be ${warmUpDiscussionLevel}.

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "title": "Warm-Up Discussion",
  "level": "${warmUpDiscussionLevel}",
  "questions": [
    {
      "question": "Discussion question here",
      "hints": "Optional hints or prompts for students"
    }
  ]
}

Topic/Text/Video: ${sourceText}`;



      const contentRaw = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: 800 });

      if (contentRaw) {
        try {
          let content = contentRaw.trim();
          // Удаляем markdown-блоки, если есть
          if (content.startsWith('```json')) {
            content = content.replace(/^```json\n/, '').replace(/\n```$/, '');
          } else if (content.startsWith('```')) {
            content = content.replace(/^```\n/, '').replace(/\n```$/, '');
          }
          const parsed = JSON.parse(content);

          if (parsed.questions && Array.isArray(parsed.questions)) {
            setGeneratedWarmUpDiscussion(parsed);
          }
        } catch (parseError) {
          // Error parsing Warm Up Discussion JSON
        }
      }
    } catch (e) {
      // Error in handleWarmUpDiscussionGenerate
    } finally {
      setWarmUpDiscussionLoading(false);
    }
  };

  const handleWarmUpDiscussionInsert = () => {
    if (!generatedWarmUpDiscussion) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedWarmUpDiscussion, 'warmupdiscussion');
      setShowWarmUpDiscussionGenerator(false);
      setWarmUpDiscussionInput("");
      setWarmUpDiscussionCount(5);
      setGeneratedWarmUpDiscussion(null);
      setSelectedTemplate(null);
      return;
    }
    if (!activeEditor) return;
    activeEditor.chain().focus().insertWarmUpDiscussionExercise({
      questions: generatedWarmUpDiscussion.questions,
      title: generatedWarmUpDiscussion.title,
      level: generatedWarmUpDiscussion.level
    }).run();

    setShowWarmUpDiscussionGenerator(false);
    setWarmUpDiscussionInput("");
    setWarmUpDiscussionCount(5);
    setGeneratedWarmUpDiscussion(null);
    setSelectedTemplate(null);
  };

  const handleWarmUpDiscussionCancel = () => {
    setShowWarmUpDiscussionGenerator(false);
    setWarmUpDiscussionInput("");
    setGeneratedWarmUpDiscussion(null);
    setWarmUpDiscussionCount(5);
    setSelectedTemplate(null);
  };

  return { handleWarmUpDiscussionGenerate, handleWarmUpDiscussionInsert, handleWarmUpDiscussionCancel };
}
